// chill-reactions: overlay-style reaction picker on product images.
//
// Sprint 2.5 redesign. Replaces the standalone PDP section with an
// Instagram-style overlay sitting ON the product photo. Two states:
//   COMPACT — glass pill at corner shows top reaction + total
//   EXPANDED — 4-emoji row slides up; user picks; row collapses
//
// Plus double-tap-to-react on the PDP gallery image (defaults to ✦).
//
// 100% reuse of Phase 3B data layer:
//   - customer.metafields.chillandout.reactions (per-customer state)
//   - product.metafields.chillandout.reactions_summary (aggregate)
//   - chillandoutCustomerAPI.setReaction (queue+emit)
//   - chillandoutPoints.emit('reaction:added') for +5 ✦
//
// Spec: docs/MASTER-BRIEF.md §11.2
// Voice: docs/VOICE-EXAMPLES.md
// Budget: < 2.5 KB gzip. No deps.

(function () {
  'use strict';

  if (window.chillandoutReactionsLoaded) return;
  window.chillandoutReactionsLoaded = true;

  const THROTTLE_MS = 60 * 1000; // 1 change/min/customer/product
  const PULSE_MS = 600;
  const DOUBLE_TAP_WINDOW_MS = 350;
  const ROW_AUTO_CLOSE_MS = 4000;
  const LOCAL_KEY_PREFIX = 'chillandout:reactions:throttle:';

  const EMOJI_MAP = {
    fire: '🔥',
    glyph: '✦',
    wave: '🌊',
    heart: '❤️',
  };

  const CANONICAL = ['fire', 'glyph', 'wave', 'heart'];

  function nowMs() { return Date.now(); }

  function readThrottle(productId) {
    try {
      const raw = localStorage.getItem(LOCAL_KEY_PREFIX + productId);
      const t = parseInt(raw, 10);
      return Number.isFinite(t) ? t : 0;
    } catch { return 0; }
  }

  function writeThrottle(productId, ts) {
    try { localStorage.setItem(LOCAL_KEY_PREFIX + productId, String(ts)); } catch {}
  }

  function isLoggedIn() {
    return !!document.body?.dataset.customerId;
  }

  /* ---------- web component ---------- */

  class ChillReactionsOverlay extends HTMLElement {
    constructor() {
      super();
      this._wired = false;
      this._productId = null;
      this._currentChoice = null;
      this._pill = null;
      this._row = null;
      this._burst = null;
      this._autoCloseTimer = 0;
      this._onPillClick = this._onPillClick.bind(this);
      this._onRowClick = this._onRowClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
    }

    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this._productId = this.dataset.productId;
      this._currentChoice = this.dataset.customerChoice || null;
      this._pill = this.querySelector('[data-reactions-pill]');
      this._row = this.querySelector('[data-reactions-row]');
      this._burst = this.querySelector('[data-reactions-burst]');

      if (this._pill) this._pill.addEventListener('click', this._onPillClick);
      if (this._row) this._row.addEventListener('click', this._onRowClick);
    }

    disconnectedCallback() {
      if (this._pill) this._pill.removeEventListener('click', this._onPillClick);
      if (this._row) this._row.removeEventListener('click', this._onRowClick);
      document.removeEventListener('click', this._onDocClick);
      if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);
    }

    /* ---------- pill expand/collapse ---------- */

    _onPillClick(e) {
      e.stopPropagation();
      const expanded = !this._row.hasAttribute('hidden');
      if (expanded) this._collapse();
      else this._expand();
    }

    _expand() {
      this._row.removeAttribute('hidden');
      this._pill.setAttribute('aria-expanded', 'true');
      // Auto-close after idle to avoid lingering UI.
      if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);
      this._autoCloseTimer = setTimeout(() => this._collapse(), ROW_AUTO_CLOSE_MS);
      // Click-outside collapses.
      setTimeout(() => document.addEventListener('click', this._onDocClick), 0);
    }

    _collapse() {
      this._row.setAttribute('hidden', '');
      this._pill.setAttribute('aria-expanded', 'false');
      if (this._autoCloseTimer) clearTimeout(this._autoCloseTimer);
      document.removeEventListener('click', this._onDocClick);
    }

    _onDocClick(e) {
      if (this.contains(e.target)) return;
      this._collapse();
    }

    /* ---------- emoji button click ---------- */

    _onRowClick(e) {
      const btn = e.target.closest('[data-reaction]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const reactionKey = btn.dataset.reaction;

      if (!isLoggedIn()) {
        window.location.href = '/account/login?return_url=' +
          encodeURIComponent(window.location.pathname);
        return;
      }

      const last = readThrottle(this._productId);
      if (nowMs() - last < THROTTLE_MS) {
        if (window.chillandoutToast) {
          window.chillandoutToast.show({
            message: 'easy ✦ give it a sec',
            durationMs: 1600,
          });
        }
        return;
      }

      this._toggle(reactionKey, btn);
    }

    _toggle(reactionKey, btn) {
      const wasSelected = this._currentChoice === reactionKey;
      const previousChoice = this._currentChoice;

      // Clear all selections.
      this._row.querySelectorAll('[data-reaction]').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });

      if (!wasSelected) {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        this._adjustCount(reactionKey, 1);
        this._currentChoice = reactionKey;
        this._pulse(btn);
        this._burstFrom(btn, EMOJI_MAP[reactionKey]);
        this._fireFeedback(reactionKey);
        // Update pill display.
        this._updatePillDisplay(reactionKey, true);
      } else {
        // Unselect.
        this._adjustCount(reactionKey, -1);
        this._currentChoice = null;
        this._updatePillDisplay(null, false);
      }

      // If switching reaction, decrement old.
      if (previousChoice && previousChoice !== reactionKey && !wasSelected) {
        this._adjustCount(previousChoice, -1);
      }

      writeThrottle(this._productId, nowMs());

      // Auto-collapse the row after a moment so the result is visible briefly.
      setTimeout(() => this._collapse(), 800);

      const api = window.chillandoutCustomerAPI;
      if (api && typeof api.setReaction === 'function') {
        api.setReaction(this._productId, this._currentChoice).catch(() => {});
      }
    }

    _adjustCount(reactionKey, delta) {
      const item = this._row.querySelector('[data-reaction="' + reactionKey + '"]');
      if (!item) return;
      const display = item.querySelector('[data-count-display]');
      if (display) {
        const current = parseInt(item.dataset.count, 10) || 0;
        const next = Math.max(0, current + delta);
        item.dataset.count = String(next);
        display.textContent = String(next);
      }
      // Update total on host.
      const total = (parseInt(this.dataset.total, 10) || 0) + delta;
      this.dataset.total = String(Math.max(0, total));
      // Update pill count.
      const pillCount = this._pill.querySelector('[data-pill-count]');
      const newTotal = Math.max(0, total);
      if (pillCount) {
        if (newTotal > 0) pillCount.textContent = String(newTotal);
        else pillCount.remove();
      } else if (newTotal > 0) {
        const span = document.createElement('span');
        span.className = 'chill-reactions-overlay__pill-count';
        span.dataset.pillCount = '';
        span.textContent = String(newTotal);
        this._pill.appendChild(span);
      }
    }

    _updatePillDisplay(reactionKey, isYours) {
      const emojiEl = this._pill.querySelector('[data-pill-emoji]');
      if (!emojiEl) return;
      if (reactionKey) {
        emojiEl.textContent = EMOJI_MAP[reactionKey];
        this._pill.classList.add('is-yours');
      } else {
        // Fall back to top-counted emoji or canonical ✦.
        const top = this._computeTop();
        emojiEl.textContent = EMOJI_MAP[top] || '✦';
        this._pill.classList.remove('is-yours');
      }
    }

    _computeTop() {
      let topKey = 'glyph';
      let topCount = 0;
      this._row.querySelectorAll('[data-reaction]').forEach((item) => {
        const c = parseInt(item.dataset.count, 10) || 0;
        if (c > topCount) {
          topCount = c;
          topKey = item.dataset.reaction;
        }
      });
      return topCount > 0 ? topKey : 'glyph';
    }

    _pulse(btn) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      btn.classList.remove('is-pulsing');
      void btn.offsetWidth;
      btn.classList.add('is-pulsing');
      setTimeout(() => btn.classList.remove('is-pulsing'), PULSE_MS);
    }

    _burstFrom(btn, emoji) {
      if (!this._burst) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const btnRect = btn.getBoundingClientRect();
      const hostRect = this.getBoundingClientRect();
      const x = btnRect.left + btnRect.width / 2 - hostRect.left;
      const y = btnRect.top - hostRect.top;
      // Spawn 5 particles at angles, fly outward.
      for (let i = 0; i < 5; i++) {
        const p = document.createElement('span');
        p.className = 'chill-reactions-overlay__burst-particle';
        p.textContent = emoji;
        p.style.left = x + 'px';
        p.style.top = y + 'px';
        const angle = -Math.PI / 2 + (i - 2) * 0.35; // upward fan
        const dist = 60 + Math.random() * 30;
        const bx = Math.cos(angle) * dist;
        const by = Math.sin(angle) * dist;
        const rot = (Math.random() - 0.5) * 60;
        p.style.setProperty('--burst-x', bx + 'px');
        p.style.setProperty('--burst-y', by + 'px');
        p.style.setProperty('--burst-r', rot + 'deg');
        this._burst.appendChild(p);
        setTimeout(() => p.remove(), 800);
      }
    }

    _fireFeedback(reactionKey) {
      if (window.chillandoutToast) {
        window.chillandoutToast.show({
          message: 'reaction saved',
          glyph: '✦',
          durationMs: 1400,
        });
      }
      document.dispatchEvent(new CustomEvent('chillandout:play-sound', {
        detail: { name: 'chime' },
      }));
      if (window.chillandoutPoints && typeof window.chillandoutPoints.emit === 'function') {
        window.chillandoutPoints.emit('reaction:added', {
          productId: this._productId,
          reaction: reactionKey,
        });
      }
    }
  }

  if (!customElements.get('chill-reactions-overlay')) {
    customElements.define('chill-reactions-overlay', ChillReactionsOverlay);
  }

  /* ---------- double-tap-to-react on PDP gallery image ---------- */
  //
  // Sprint 2.5 addition. Listens on any element with [data-chill-doubletap-host]
  // (PDP gallery image wrapper). Two taps within 350ms → react with ✦
  // (the brand glyph as the "default like"). Big italic ✦ pulse animation
  // appears at tap point. Uses the same overlay state for actual mutation.

  function bindDoubleTap(host) {
    if (host.dataset.dblTapWired === '1') return;
    host.dataset.dblTapWired = '1';

    let lastTap = 0;
    let lastX = 0;
    let lastY = 0;

    function fire(x, y) {
      // Find the nearest reactions overlay (sibling or descendant of host's
      // parent product container).
      const productEl = host.closest('[data-product-id]') || host.closest('main') || document;
      const overlay = productEl.querySelector('chill-reactions-overlay');
      if (!overlay) return;

      // Big ✦ burst at tap point.
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const burst = document.createElement('span');
        burst.className = 'chill-reactions-overlay__double-tap-burst';
        burst.textContent = '✦';
        burst.setAttribute('aria-hidden', 'true');
        const hostRect = host.getBoundingClientRect();
        burst.style.left = (x - hostRect.left) + 'px';
        burst.style.top = (y - hostRect.top) + 'px';
        // Ensure host has positioning context.
        const cs = getComputedStyle(host);
        if (cs.position === 'static') host.style.position = 'relative';
        host.appendChild(burst);
        setTimeout(() => burst.remove(), 800);
      }

      // Trigger the glyph reaction via the overlay's row.
      const glyphBtn = overlay.querySelector('[data-reaction="glyph"]');
      if (glyphBtn) {
        // Synthesize a click on the glyph button — reuses all guard logic
        // (login redirect, throttle, points emission, persistence).
        glyphBtn.click();
      }
    }

    host.addEventListener('click', (e) => {
      const now = nowMs();
      const dt = now - lastTap;
      const dx = Math.abs(e.clientX - lastX);
      const dy = Math.abs(e.clientY - lastY);
      if (dt < DOUBLE_TAP_WINDOW_MS && dx < 30 && dy < 30) {
        // Second tap of a double-tap.
        e.preventDefault();
        e.stopPropagation();
        fire(e.clientX, e.clientY);
        lastTap = 0; // reset
        return;
      }
      lastTap = now;
      lastX = e.clientX;
      lastY = e.clientY;
    });
  }

  function bindAllDoubleTapHosts() {
    document.querySelectorAll('[data-chill-doubletap-host]').forEach(bindDoubleTap);
  }

  /* ---------- PDP overlay teleport ----------
   *
   * Sprint 2.5: the chill-product-storytelling section renders the reactions
   * overlay inside a <template data-chill-pdp-reactions-carrier> as a hidden
   * carrier. We teleport it into Horizon's product gallery on
   * DOMContentLoaded, where it floats over the image as designed (§11.2).
   *
   * Horizon's gallery markup uses .product-media-gallery as the wrapper
   * (verified Horizon 2024). If that selector ever changes upstream, we
   * fall back to .product-media or .media-gallery, then to the first
   * <img> inside the product info block.
   */

  function teleportPdpOverlay() {
    const carrier = document.querySelector('template[data-chill-pdp-reactions-carrier]');
    if (!carrier) return;

    // Find the product gallery host. Horizon uses <media-gallery> as the
    // custom-element wrapper around all product images. Fall back to other
    // common patterns for non-Horizon variants.
    const candidates = [
      'media-gallery',                         // Horizon custom element (verified)
      '.media-gallery__grid',                  // Horizon inner grid
      '.product-media-gallery',                // legacy theme convention
      '.product-media',                        // alternate variant
      '.media-gallery',                        // alternate variant
      '[data-product-media]',                  // generic hook
    ];
    let host = null;
    for (const sel of candidates) {
      host = document.querySelector(sel);
      if (host) break;
    }
    if (!host) return; // no gallery → bail (defensive)

    // Ensure positioning context.
    const cs = getComputedStyle(host);
    if (cs.position === 'static') host.style.position = 'relative';

    // Clone overlay from template, append into gallery.
    const overlay = carrier.content.firstElementChild?.cloneNode(true);
    if (!overlay) return;
    host.appendChild(overlay);

    // Mark the gallery as a double-tap host (the actual <img> would be more
    // precise but the wrapper works because clicks bubble up).
    host.setAttribute('data-chill-doubletap-host', '');
    bindDoubleTap(host);

    // Remove carrier (no need to keep it in DOM after teleport).
    carrier.remove();
  }

  function init() {
    bindAllDoubleTapHosts();
    teleportPdpOverlay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
